import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Target } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

const tone = (v) => (v >= 75 ? 'bg-emerald-100 text-emerald-800 border-emerald-200' : v >= 50 ? 'bg-amber-100 text-amber-800 border-amber-200' : 'bg-red-100 text-red-700 border-red-200');
const bar = (v) => (v >= 75 ? 'bg-emerald-500' : v >= 50 ? 'bg-amber-500' : 'bg-red-500');
const PARTS = [['experiencia', 'Experiência', 40], ['formacao', 'Formação', 25], ['conhecimentos', 'Conhecimentos', 35]];

export const useMatches = (request, key) => {
  const [matches, setMatches] = useState({});
  const tries = useRef(0);
  useEffect(() => {
    if (!key) return undefined;
    let alive = true;
    let timer;
    tries.current = 0;
    const run = () => request().then((r) => {
      if (!alive) return;
      setMatches(r.data.matches || {});
      const pending = Object.values(r.data.matches || {}).some((m) => m.status === 'calculando');
      if (pending && tries.current++ < 20) timer = setTimeout(run, 4000);
    }).catch(() => {});
    run();
    return () => { alive = false; clearTimeout(timer); };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return matches;
};

export const MatchBadge = ({ match, testid, compact }) => {
  if (!match) return null;
  if (match.status === 'calculando') {
    return <span className="inline-flex items-center gap-1 text-xs text-slate-500" data-testid={`${testid}-loading`}><Loader2 className="w-3 h-3 animate-spin" />{compact ? 'Aderência…' : 'Calculando aderência…'}</span>;
  }
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" onClick={(e) => e.stopPropagation()} data-testid={testid}
          className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold transition-transform hover:scale-105 ${tone(match.total)}`}>
          <Target className="w-3 h-3" />{match.total}%{!compact && ' aderência'}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 space-y-3" onClick={(e) => e.stopPropagation()} data-testid={`${testid}-details`}>
        <div>
          <p className="font-semibold text-slate-900">Aderência ao perfil do cargo: {match.total}%</p>
          {match.resumo && <p className="text-xs text-slate-600 mt-0.5">{match.resumo}</p>}
        </div>
        {PARTS.map(([k, label, w]) => (
          <div key={k}>
            <div className="flex justify-between text-xs text-slate-600"><span>{label} <span className="text-slate-400">(peso {w}%)</span></span><span className="font-semibold">{match[k]}%</span></div>
            <div className="h-1.5 rounded-full bg-slate-100 mt-1"><div className={`h-1.5 rounded-full ${bar(match[k])}`} style={{ width: `${match[k]}%` }} /></div>
          </div>
        ))}
        {match.pontos_fortes?.length > 0 && (
          <div><p className="text-xs font-semibold text-emerald-700">Pontos fortes</p>
            <ul className="text-xs text-slate-600 list-disc pl-4">{match.pontos_fortes.map((p, i) => <li key={i}>{p}</li>)}</ul></div>
        )}
        {match.lacunas?.length > 0 && (
          <div><p className="text-xs font-semibold text-red-600">O que falta</p>
            <ul className="text-xs text-slate-600 list-disc pl-4">{match.lacunas.map((p, i) => <li key={i}>{p}</li>)}</ul></div>
        )}
        {match.sem_perfil_cargo && (
          <p className="text-xs text-amber-700 bg-amber-50 rounded p-1.5" data-testid={`${testid}-no-position`}>Vaga sem Engenharia de Cargo: estimativa feita só pela descrição da vaga.</p>
        )}
        <p className="text-xs text-slate-500">{match.por_ia === false ? 'Perfil sem dados de currículo para comparar.' : 'Calculado por IA com base no currículo e no perfil do cargo.'}</p>
      </PopoverContent>
    </Popover>
  );
};
